import Golfer, { LoftForFitting, Loft, ShaftLeanStatus } from '../classes/Golfer';
import TagManager from 'react-gtm-module';
import Fitter from '../classes/Fitter';
import * as ShotSelection from '../classes/ShotSelection';
import ISimulator from '../classes/ISimulator';
import SimulatorData from '../classes/SimulatorData';

const TrackManAdjustments = {
    dynamicLoftAdjustment: 2.7,
    attackAngleMinimum: -11,
    attackAngleMaximum: 0,
    attackAngleA: -0.0684,
    attackAngleB: -0.7367,
    attackAngleC: 0.1783,
    attackAngleNull_DLMultiplier: -.42,
    attackAngleNull_Modifier: -1.84,
    dynamicLoftNull_AAMultiplier: -1.07,
    dynamicLoftNull_Modifier: 3.56
}

export class ActiveShotCoordinate {
    row: number;
    col: number;

    constructor(row: number, col: number) {
        this.row = row;
        this.col = col;
    }
}

//just need a simple class for passing around attack angle and shaft lean values between functions
class AA_SL {
    constructor(public attackAngle: string, public shaftLean: string) { };
}

//Determine if there are dyanamic loft values in the practice shots hit so far. This affects the shaft lean y-axis on the Technique Chart
export const getShaftLeanStatus = (modelType: string, loftsForFitting: LoftForFitting[]): ShaftLeanStatus => {
    //test doesn't apply if the not Foresight device b/c it always has dynamic loft
    if (modelType === "TrackMan"){
        return "available";
    }

    //if no shots taken yet, then status is "unknown"
    if (modelType === "Foresight"){
        let totalShots = 0;
        loftsForFitting.forEach(loft => {
            totalShots += loft.practiceShots.filter(l => l.status === "closed").length;
        })
        if (totalShots === 0){
            return "unknown";
        }
    }   
    
    //if shots where taken, determine if any have a dynamic loft
    let noDynamicLoft = true;
    if (modelType === "Foresight") {
        loftsForFitting.forEach(loft => {
            // if ANY shot has a dynamic loft, then set the flag to false
            if (loft.practiceShots.filter(l => l.data?.dynamicLoft !== undefined).length > 0) {
                noDynamicLoft = false;
            }
        })
    }
    return noDynamicLoft ? "unavailable" : "available";
}

//given all the practice shots taken so far, calcuate the plot axis values for dynamic loft and attack angle for each loft.
export const calculateShotsToPlot = (modelType: string, loftsForFitting: LoftForFitting[], fitter: Fitter, simulator: ISimulator): Map<string, ShotSelection.ShotToPlot> => {
    let shotsToPlot = (new Map<string, ShotSelection.ShotToPlot>());
    let shaftLeanStatus = getShaftLeanStatus(modelType, loftsForFitting);

    loftsForFitting.forEach(loft => {
        let shotAbbreviation = loft.shotType.substring(0, 1);
        let shot: ShotSelection.ShotToPlot | undefined;
        shot = shotsToPlot.get(shotAbbreviation);
        //if shot abbreviation not in dictionary, create it
        if (shot === undefined) {
            shotsToPlot.set(shotAbbreviation, { attackAngle: 0, shaftLean: 0, totalShots: 0 } as ShotSelection.ShotToPlot);
            shot = shotsToPlot.get(shotAbbreviation);
        }
        //loop through all practice shots and add them to shotsToPlot
        loft.practiceShots.forEach(ps => {
            if (ps.status == 'closed' && ps.data) {
                addShotToPlotCollection(shaftLeanStatus, loft.loft, loft.shotType, ps.data, shot!, fitter, simulator);
            }
        });
    });
    return shotsToPlot;
}

//apply Titlest adjustments and then add the shot to the collection. shotToPlot is updated by reference
const addShotToPlotCollection = (shaftLeanStatus: ShaftLeanStatus , loft: Loft, shotType: string, payload: SimulatorData, shotToPlot: ShotSelection.ShotToPlot, fitter: Fitter, simulator: ISimulator): void => {
    let aa_sl = simulator.calculateAttackAngleShaftLean(loft, shotType, payload.attackAngle, payload.dynamicLoft, true, true, fitter);
    //if there is no shaft lean then override the shaft lean values to three set axis points based on shot type
    if (shaftLeanStatus === "unavailable") {
        switch (shotType) {
            case "Full":
                aa_sl.shaftLean = "12.5";
                break;
            case "Pitch":
                aa_sl.shaftLean = "7.75";
                break;
            case "Open Face":
                aa_sl.shaftLean = "3";
                break;
        }
    }

    if (aa_sl.attackAngle !== "--" && aa_sl.shaftLean !== "--") {
        shotToPlot.attackAngle += Number(aa_sl.attackAngle);
        shotToPlot.shaftLean += Number(aa_sl.shaftLean);
        shotToPlot.totalShots++;
    }
}

//reset active shots b/c as user toggles between pages, the previous active shot is stored in the local storage and it needs to be cleared
export const resetActiveShotFlags = (golfer: Golfer): void => {
    let row = 0;
    let col: number;

    while (row < golfer.loftsForFitting.length) {
        col = 0;
        while (col < golfer.loftsForFitting[row].practiceShots.length) {
            if (golfer.loftsForFitting[row].practiceShots[col].status == 'active') {
                golfer.loftsForFitting[row].practiceShots[col].status = 'open';
            }
            col++;
        }
        row++;
    }
}

//find the next active shot in the list.
//both activeShotCoordinate and the golfer object are updated by reference;
//return true if a new shot is assigned

export const calculateNextActiveShot = (activeShotCoordinate: ActiveShotCoordinate, golfer: Golfer): boolean => {
    let shotFound = false;

    //guard clause: if current row is -1 then the all shots have been taken so nothing to do
    if (activeShotCoordinate.row != -1) {
        //start at the current row and find the next available shot. If we get to the end with nothing, then loop to the top.
        let row = activeShotCoordinate.row;
        let col: number;

        while (row < golfer.loftsForFitting.length && !shotFound) {
            col = 0;
            while (col < golfer.loftsForFitting[row].practiceShots.length && !shotFound) {
                if (golfer.loftsForFitting[row].practiceShots[col].status == 'open') {
                    golfer.loftsForFitting[row].practiceShots[col].status = 'active';
                    activeShotCoordinate.row = row;
                    activeShotCoordinate.col = col;
                    shotFound = true;
                }
                col++;
            }
            row++;
        }

        //if nothing found going to the end, the restart at top and only go to the current row.
        if (!shotFound) {
            row = 0;
            while (row < activeShotCoordinate.row && !shotFound) {
                col = 0;
                while (col < golfer.loftsForFitting[row].practiceShots.length && !shotFound) {
                    if (golfer.loftsForFitting[row].practiceShots[col].status == 'open') {
                        golfer.loftsForFitting[row].practiceShots[col].status = 'active';
                        activeShotCoordinate.row = row;
                        activeShotCoordinate.col = col;
                        shotFound = true;
                    }
                    col++;
                }
                row++;
            }
        }
        //no shots left to hit. 'null' out the coordinates
        if (!shotFound) {
            activeShotCoordinate.row = -1;
            activeShotCoordinate.col = -1;
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_shot_finished',
                }
            });    
        }
    }
    return shotFound;
}

//find the next open shot for the row just clicked on. If they are all filled then add a new shot
export const setActiveShotForCurrentRow = (activeShotCoordinate: ActiveShotCoordinate, golfer: Golfer): boolean => {
    let shotFound = false;
    const row = activeShotCoordinate.row;  //alias for ease of typing
    let col = 0;

    //guard clause: if current row is -1 then the all shots have been taken so nothing to do
    if (activeShotCoordinate.row != -1) {
        while (col < golfer.loftsForFitting[row].practiceShots.length && !shotFound) {
            if (golfer.loftsForFitting[row].practiceShots[col].status == 'open') {
                golfer.loftsForFitting[row].practiceShots[col].status = 'active';
                activeShotCoordinate.row = row;
                activeShotCoordinate.col = col;
                shotFound = true;
            } else {
                col++;
            }
        }
    }
    //if shot not found, then add a shot to this row and make it active
    if (!shotFound) {
        //        golfer.loftsForFitting[row].statuses.push('active');
        golfer.loftsForFitting[row].practiceShots.push(new ShotSelection.PracticeShot('active'));
        activeShotCoordinate.col = col;
    }
    return shotFound;
}

//remove the shot at the specified location
export const deleteShot = (activeShooterCoordinate: ActiveShotCoordinate, golfer: Golfer) => {
    golfer.loftsForFitting[activeShooterCoordinate.row].practiceShots[activeShooterCoordinate.col].status = 'open';
}

export const getNowHittingTitle = (activeShotCoordinate: ActiveShotCoordinate, golfer: Golfer, intl: any): string => {
    let title = '';
    if (golfer.loftsForFitting.length > 0) {
        title = activeShotCoordinate.row != -1 ? intl.formatMessage({id: "now.hitting.title"}) + ": " + golfer.loftsForFitting[activeShotCoordinate.row].loft + ' ' + intl.formatMessage({id: "shot.selection." + golfer.loftsForFitting[activeShotCoordinate.row].shotType.replace(' ', '').toLowerCase() }) : intl.formatMessage({id: "now.hitting.finished.fitting" });
    }
    return title;
}

export const getNowHittingYards = (activeShooterCoordinate: ActiveShotCoordinate, golfer: Golfer, fitter: Fitter, intl: any): string => {
    let yards = '';
    let intlYds = '';
    let intl1520 = '';
    let intl1015 = '';
    if (fitter.distanceUnits === "Yards"){
        //yards
        intlYds = intl.formatMessage({id: "now.hitting.yds"});
        intl1015 = "10-15";
        intl1520 = "15-20";
    } else {
        //meters
        intlYds = intl.formatMessage({id: "now.hitting.meters"});
        intl1015 = "9-14";
        intl1520 = "14-18";
    }
    if (golfer.loftsForFitting.length > 0) {
        if (activeShooterCoordinate.row > -1) {
            switch (golfer.loftsForFitting[activeShooterCoordinate.row].shotType) {
                case "Pitch":
                    yards = `${intl1520} ${intlYds}`;
                    break;
                case "Open Face":
                    yards = `${intl1015} ${intlYds}`;
                    break;
            }
        }
    }
    return yards;
}

//giving a shot Id, determine if it was already used in the previous practice shots
export const shotIdIsUnique = (shotId: string, golfer: Golfer): boolean => {
    let found = false;
    golfer.loftsForFitting.forEach(loft => {
        loft.practiceShots.forEach(shot => {
            if (shot.data?.Id === shotId) {
                found = true;
            }
        })
    })
    return !found;
}

export const deleteShotById = (id: string, golfer: Golfer) => {
    //delete the shot from the Your Shots collection
    golfer.loftsForFitting.forEach(loft => {
        loft.practiceShots.forEach(ps => {
            if (ps.data && ps.data.Id == id) {
                ps.data = undefined;
                ps.status = 'open';
            }
        })
    });
    //flag it as deleted in teh ShotHistory collection (used only for the enhanced json data object sent to the back-end server)
    golfer.enhancedFittingData.forEach(shot => {
        if (shot.id === id){
            shot.isDeleted = "true";
        }
    })
}

//given a golfer object, create a shot history collection based on loftsForFitting collection
export const populateShotHistoryFromGolfer = (golfer: Golfer, fitter: Fitter, simulator: ISimulator, intl: any): ShotSelection.ShotHistory[] => {
    let shotHistory = new Array<ShotSelection.ShotHistory>();
    let counter = 1;

    golfer.loftsForFitting.forEach(loft => {
        loft.practiceShots.forEach(ps => {
            if (ps.data) {
                let payload = ps.data;
                let carryDistance = simulator.modelType === "trackman" ? payload.carryDistance.toFixed(0).toString() : "n/a";
                let aa_sl = simulator.calculateAttackAngleShaftLean(loft.loft, loft.shotType, payload.attackAngle, payload.dynamicLoft, false, true);
                let shot = newShotHistoryRecord(ps.data.Id, counter++, loft.loft, intl.formatMessage({id: "shot.selection." + loft.shotType.replace(" ", "").toLowerCase()}), payload.dynamicLoft !== undefined ? payload.dynamicLoft : 0, aa_sl.attackAngle, aa_sl.shaftLean,
                    payload.ballSpeed, payload.launchAngle, payload.backSpin, carryDistance, payload.clubSpeed, fitter, intl);
                shotHistory.push(shot);
            }
        })
    })
    //sort the shotHistory in descending order
    shotHistory.sort((a, b) => { return b.shotNumber - a.shotNumber })

    return shotHistory;
}

export const convertUnits = (data: SimulatorData, simulator: ISimulator, fitter: Fitter) => {
    if (simulator && fitter) {
        if (fitter.speedUnits === "MPH") {
            data.ballSpeed = data.ballSpeed * 2.237;
            data.clubSpeed = data.clubSpeed * 2.237;
        }
        if (fitter.distanceUnits === "Yards") {
            data.carryDistance = data.carryDistance * 1.09361;
        }
    }
}

export const newShotHistoryRecord = (id: string, shotNumber: number, loft: string, shotType: string, dynamicLoft: number, attackAngle: string, shaftLean: string, ballSpeed: number, launchAngle: number,
    backSpin: number, carryDistance: string, clubSpeed: number, fitter: Fitter, intl: any): ShotSelection.ShotHistory => {

    let shotHistory = new ShotSelection.ShotHistory(id, shotNumber, loft, shotType, dynamicLoft, attackAngle, shaftLean, ballSpeed.toFixed(0), Number(launchAngle.toFixed(0)), Number(backSpin.toFixed(0)), carryDistance, clubSpeed.toFixed(0));
    return shotHistory;
}

//add a new practice shot to the shot history table. Shot History collection is updated by ref
export const addShotToHistory = async (loft: Loft, shotType: string, shotData: SimulatorData, fitter: Fitter, simulator: ISimulator, shotHistory: ShotSelection.ShotHistory[], intl: any) => {
    //get the next shot number since they can bounce around a bit depending on whether user is actively deleting older shots and redoing them
    let nextShotNumber = 1;

    shotHistory.forEach(shot => {
        nextShotNumber = nextShotNumber <= shot.shotNumber ? shot.shotNumber + 1 : nextShotNumber;
    })

    let aa_sl = simulator.calculateAttackAngleShaftLean(loft, shotType, shotData.attackAngle, shotData.dynamicLoft, false, true);

    let carryDistance = simulator.modelType === "trackman" ? shotData.carryDistance.toFixed(0).toString() : "n/a";

    let newRecord = newShotHistoryRecord(shotData.Id.toString(), nextShotNumber, loft, intl.formatMessage({id: "shot.selection." + shotType.replace(" ", "").toLowerCase()}), shotData.dynamicLoft !== undefined ? shotData.dynamicLoft : 0, aa_sl.attackAngle, aa_sl.shaftLean,
        shotData.ballSpeed, shotData.launchAngle, shotData.backSpin, carryDistance, shotData.clubSpeed, fitter, intl);
    //insert at beginning of array b/c we want to see most recent at top of the grid
    shotHistory.unshift(newRecord);
}
